import type { ImportDto, ImportRowsResponse } from '@performance-crm/shared';
import { fmt } from '@/lib/format';
import { downloadWorkbook, type SheetCell } from '@/lib/spreadsheet';

const RESULT: Record<string, string> = {
  PROCESADO: 'Lead nuevo',
  DUPLICADO: 'Recontacto',
  ERROR: 'Error',
  PENDIENTE: 'Pendiente',
};
const DETAIL: Record<string, string> = {
  PROCESADO: 'Lead creado',
  DUPLICADO: 'Anotado en su lead activo',
  PENDIENTE: 'Sin procesar todavía',
};

const slug = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'importacion';

/** Excel con el detalle de una importación: cada fila del archivo con su resultado (lead creado / recontacto / error) y un resumen. */
export async function downloadImportDetail(
  imp: ImportDto,
  rows: ImportRowsResponse,
): Promise<void> {
  const origin = window.location.origin;
  const detail: SheetCell[][] = rows.items.map((r) => [
    r.row,
    RESULT[r.status] ?? r.status,
    r.error ?? DETAIL[r.status] ?? '',
    r.leadName ?? '',
    r.leadId ? `${origin}/leads/${r.leadId}` : '',
    ...rows.headers.map((h) => r.data[h] ?? ''),
  ]);
  const byError = new Map<string, number>();
  for (const r of rows.items)
    if (r.status === 'ERROR') {
      const key = r.error ?? 'Error sin detalle';
      byError.set(key, (byError.get(key) ?? 0) + 1);
    }
  const summary: SheetCell[][] = [
    ['Importación', imp.name],
    ['Estado', imp.status],
    ['Cargada', fmt.dateTimeFull(imp.createdAt)],
    ['Por', imp.createdByName ?? ''],
    ['Proyecto por defecto', imp.projectName ?? 'Por fila'],
    ['Asesor por defecto', imp.advisorName ?? 'Por turnos / por fila'],
    ['Filas', imp.totalRows],
    ['Leads nuevos', imp.created],
    ['Recontactos', imp.recontacts],
    ['Errores', imp.errors],
    [],
    ['Motivo de error', 'Filas'],
    ...[...byError.entries()].sort((a, b) => b[1] - a[1]).map(([e, n]) => [e, n] as SheetCell[]),
  ];
  await downloadWorkbook(
    [
      {
        name: 'Filas',
        header: ['Fila', 'Resultado', 'Detalle', 'Lead', 'Enlace al lead', ...rows.headers],
        rows: detail,
      },
      { name: 'Resumen', header: ['Campo', 'Valor'], rows: summary },
    ],
    `importacion-${slug(imp.name)}-${imp.createdAt.slice(0, 10)}.xlsx`,
  );
}
